import { useState } from "react";
import { Link } from "react-router";
import { Notice } from "../../design/components";
import { ApiError } from "../../api/client";
import { usePortalReprocess, type PortalDocumentRow } from "../../api/portal";
import { relative } from "../../lib/format";
import { stageLabel } from "../documents/StageStrip";
import { canReprocess, canReprocessRow, documentName, isHeld, useAttention, usePortalRole } from "./usePortal";
import styles from "./portal.module.css";

type Outcome = { kind: "ok" | "fail"; text: string };

/** The inbound off-ramps have no place on the stage strip, so they get their own words here. */
const HOLD_LABEL: Record<string, string> = {
  held_unrouted: "Held (no matching company)",
  held_exception: "Held (exception)",
  rejected: "Rejected",
};

function stoppedAt(row: PortalDocumentRow): string {
  return HOLD_LABEL[row.stage] ?? stageLabel(row.stage, row.direction);
}

export function reprocessFailure(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : "The document could not be reprocessed.";
}

/** The only action-bearing list on the overview: what stopped, where, and the one button that restarts it. */
export function AttentionList() {
  const role = usePortalRole();
  const attention = useAttention();
  const reprocess = usePortalReprocess();
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const actions = canReprocess(role);

  function run(row: PortalDocumentRow) {
    setOutcome(null);
    reprocess.mutate(row.id, {
      onSuccess: () => setOutcome({ kind: "ok", text: `${documentName(row)} is being reprocessed.` }),
      onError: (error) => setOutcome({ kind: "fail", text: reprocessFailure(error) }),
    });
  }

  return (
    <section className="card card-tight" aria-labelledby="attention-title">
      <div className="card-head">
        <h2 id="attention-title" className="grow">
          Needs attention
        </h2>
        {attention.hasMore ? (
          <Link className="link" to={attention.seeAllTo}>
            Showing the first {attention.rows.length}; see all
          </Link>
        ) : null}
      </div>
      {outcome ? (
        <div className="card-body">
          <Notice kind={outcome.kind}>{outcome.text}</Notice>
        </div>
      ) : null}
      {attention.isError ? (
        <div className="card-body">
          <Notice kind="fail">The list could not be loaded.</Notice>
        </div>
      ) : attention.rows.length === 0 ? (
        attention.isLoading ? null : (
          <div className="card-body">
            <Notice kind="ok">Nothing needs your attention</Notice>
          </div>
        )
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Document</th>
                <th>Stopped at</th>
                <th>Problem</th>
                <th className="nowrap">Updated</th>
                {actions ? <th aria-label="Actions" /> : null}
              </tr>
            </thead>
            <tbody>
              {attention.rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Link className="link" to={`/portal/documents/${row.id}`}>
                      {documentName(row)}
                    </Link>
                    {row.counterparty ? <div className={styles.sub}>{row.counterparty}</div> : null}
                  </td>
                  <td className="nowrap">{stoppedAt(row)}</td>
                  <td>
                    {row.errorCode ? <div className={`mono ${styles.errorCode}`}>{row.errorCode}</div> : null}
                    <div>{row.errorMessage ?? (isHeld(row) ? "Held for the central team" : "—")}</div>
                  </td>
                  <td className="nowrap">{relative(row.updatedAt)}</td>
                  {actions ? (
                    <td className={styles.actions}>
                      {canReprocessRow(role, row) ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={reprocess.isPending && reprocess.variables === row.id}
                          onClick={() => run(row)}
                        >
                          Reprocess
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
